'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { orchestratorApi } from '@/app/services/orchestrator-api.service';
import { Box, Typography, Button, Paper } from '@mui/material';

// docs/unassigned-user-access-plan.md — closes the Track A rollout scaffold
// (Authorization:UnassignedUsersSeeAllCompanies) once it's flipped to false. Until then this is
// inert: GetUserCompIdsAsync's own unassigned-user fallback means /api/user/my-companies cannot
// return empty for anyone while the flag is still true, so nothing changes for existing users yet
// (verified against AuthorizationService.cs, not assumed from the plan doc).
//
// Sits at the same app-shell level as MustChangePasswordModal, for the same reason: a check that
// only lived on the invoices page would let an unassigned user reach the Dashboard or a saved deep
// link and hit a differently-broken page instead.
//
// Must NOT block login, the forced-password-change flow, or sign-out (plan's rule #1) — the
// change-password route is explicitly exempted below, and PUBLIC_ROUTES (login/sign-out) never
// reach this component at all since App.tsx renders public routes without this wrapper.
const EXEMPT_ROUTES = ['/auth/change-password'];

type Status = 'checking' | 'allowed' | 'unassigned';

export default function UnassignedAccessGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [status, setStatus] = useState<Status>('checking');

  useEffect(() => {
    if (orchestratorApi.getIsAdmin()) {
      setStatus('allowed');
      return;
    }

    orchestratorApi
      .getMyCompanies()
      .then((companies) => setStatus(companies.length > 0 ? 'allowed' : 'unassigned'))
      // Fail-open on a transient network error — the real access boundary is server-side
      // (CanAccessCompAsync on every endpoint); this screen is a UX nicety, not the security
      // control, so a fetch hiccup must not read as "your account was unassigned."
      .catch(() => setStatus('allowed'));
  }, []);

  if (EXEMPT_ROUTES.includes(pathname ?? '')) {
    return <>{children}</>;
  }

  if (status === 'checking') {
    return null;
  }

  if (status === 'allowed') {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        p: 3,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Your account isn&apos;t assigned to a company yet.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please ask your administrator to assign one — your invoices will appear here once
          that&apos;s done.
        </Typography>
        <Button variant="outlined" href="/auth/sign-out">
          Sign out
        </Button>
      </Paper>
    </Box>
  );
}
